import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { Calendar } from "primereact/calendar";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "primereact/button";
import { classNames as cx } from "primereact/utils";
import { useMutation } from "@tanstack/react-query";
import { Dropdown } from "primereact/dropdown";

import { useToast } from "../../../Providers/ToastProvider";
import { LabelValue } from "../../../components/LabelValue/LabelValue";
import { CalendarCustomEventType } from "../../../models/JobZone";
import { requests } from "../../../services/http-common";

import { useKeycloakContext } from "../../../keycloak";
import { EmployeeRoles } from "../../../models/Employee";

import classes from "./TimeTrackingModal.module.scss";
import moment from "moment";

type TimeTrackingModalProps = {
  selectedEvent?: CalendarCustomEventType;
  closeTimeTrackingModal: () => void;
};

type TimeTrackPayload = {
  checkIn: Date;
  checkOut: Date;
  userId?: string;
  scheduleId?: number;
  date?: Date;
};

export default function TimeTrackingModal({
  selectedEvent,
  closeTimeTrackingModal,
}: TimeTrackingModalProps) {
  const { show } = useToast();

  const formSchema = Yup.object().shape({
    checkIn: Yup.date().nullable().required("Field is required"),
    checkOut: Yup.date().required("Field is required"),
    userId: Yup.string().required("Field is required"),
    date: Yup.date().required("Field is required"),
  });

  const hookForm = useForm<TimeTrackPayload>({
    resolver: yupResolver(formSchema),
    shouldFocusError: false,
    defaultValues: {
      checkIn: moment(selectedEvent?.start).startOf("day").toDate(),
      checkOut: moment(selectedEvent?.end).endOf("day").toDate(),
      date: selectedEvent?.start,
    },
  });

  const keycloakContext = useKeycloakContext();

  const role = keycloakContext.keycloak.tokenParsed?.groups[0]?.slice(1);

  const editItemRequest = useMutation({
    mutationFn: (data: TimeTrackPayload) => {
      return requests.post(`/job/checkInCheckOut`, {
        ...data,
        scheduleId: selectedEvent?.id,
      });
    },
    onSuccess: () => {
      show({
        detail: "Your check-in and check-out have been successfully recorded.",
      });
      closeTimeTrackingModal();
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = hookForm;

  const getFormErrorMessage = (name: any) => {
    return (
      // ✅ THIS IS OK
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const onSubmit = (data: TimeTrackPayload) => {
    const modifiedData = { ...data };
    delete modifiedData.date;
    editItemRequest.mutate(modifiedData);
  };

  return (
    <div>
      <div className={classes.header}>
        <h2>Time tracking</h2>
        {role === EmployeeRoles.CLEANING_TECHNICIAN ? (
          <i className="pi pi-times" />
        ) : null}
      </div>
      {role === EmployeeRoles.CLEANING_TECHNICIAN ? (
        <MobileWarning />
      ) : (
        <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
          <LabelValue className={classes.employee} label="Employee" isRequired>
            <Controller
              name="userId"
              render={({ field }) => (
                <Dropdown
                  {...field}
                  appendTo="self"
                  onChange={(option) => field.onChange(option.value)}
                  options={selectedEvent?.employees.map((e) => ({
                    label: `${e.firstName} ${e.lastName}`,
                    value: e.id,
                  }))}
                  ariaLabel="pets"
                  placeholder="Select an Employee"
                  className={cx({
                    "p-invalid": errors.userId,
                  })}
                />
              )}
              control={control}
            />
            {getFormErrorMessage("userId")}
          </LabelValue>
          <LabelValue className={classes.date} label="Date" isRequired>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <Calendar
                  id={field.name}
                  value={field.value}
                  onChange={(e) => {
                    const date = moment(e.value as Date);
                    const checkIn = moment(getValues("checkIn") as Date);
                    const checkOut = moment(getValues("checkOut") as Date);

                    setValue(
                      "checkIn",
                      checkIn
                        .clone()
                        .set({
                          month: date.month(),
                          date: date.date(),
                          year: date.year(),
                        })
                        .toDate()
                    );
                    setValue(
                      "checkOut",
                      checkOut
                        .clone()
                        .set({
                          month: date.month(),
                          date: date.date(),
                          year: date.year(),
                        })
                        .toDate()
                    );

                    field.onChange(e.value);
                  }}
                  placeholder="MM/DD/YY"
                  dateFormat="mm/dd/yy"
                  showIcon
                  showButtonBar
                  className={cx({ "p-invalid": errors.date })}
                  style={{ maxWidth: "203px" }}
                />
              )}
            />
            {getFormErrorMessage("date")}
          </LabelValue>
          <LabelValue label="Clock- In" isRequired>
            <Controller
              name="checkIn"
              render={({ field }) => (
                <Calendar
                  value={field.value}
                  required
                  hourFormat="12"
                  onChange={(e) => {
                    if (!e.value) {
                      field.onChange(e.value);
                    } else {
                      const initialDate = moment(selectedEvent?.start);
                      const currentDate = moment(e.value as Date);
                      field.onChange(
                        initialDate
                          .clone()
                          .set({
                            hour: currentDate.hour(),
                            minutes: currentDate.minutes(),
                            seconds: currentDate.seconds(),
                            milliseconds: currentDate.milliseconds(),
                          })
                          .toDate()
                      );
                    }
                  }}
                  timeOnly
                />
              )}
              control={control}
            />
            {getFormErrorMessage("checkIn")}
          </LabelValue>
          <LabelValue label="Clock- Out" isRequired>
            <Controller
              name="checkOut"
              render={({ field }) => (
                <Calendar
                  value={field.value}
                  required
                  hourFormat="12"
                  onChange={(e) => {
                    if (!e.value) {
                      field.onChange(e.value);
                    } else {
                      const initialDate = moment(selectedEvent?.end);
                      const currentDate = moment(e.value as Date);

                      field.onChange(
                        initialDate
                          .clone()
                          .set({
                            hour: currentDate.hour(),
                            minutes: currentDate.minutes(),
                            seconds: currentDate.seconds(),
                            milliseconds: currentDate.milliseconds(),
                          })
                          .toDate()
                      );
                    }
                  }}
                  timeOnly
                />
              )}
              control={control}
            />
            {getFormErrorMessage("checkOut")}
          </LabelValue>
          <div className={classes.buttons}>
            <Button
              onClick={closeTimeTrackingModal}
              label="Cancel"
              className={cx(classes.button, "p-button-text p-button-secondary")}
            />
            <Button
              onClick={handleSubmit(onSubmit)}
              label="Save"
              aria-label="save-log"
              className={cx(classes.button, "p-button-sm")}
            />
          </div>
        </form>
      )}
    </div>
  );
}

const MobileWarning = () => {
  return (
    <div className={classes.warningContainer}>
      <i className="pi pi-exclamation-circle" /> <h3>Time Tracking</h3>
      <p>
        Access for clock in and out available exclusively in the mobile version
        of the app.
      </p>
    </div>
  );
};
